/**
 * Reads a public line sheet's branded presentation from plg-go-be (KP-4545).
 *
 * The page asks once, in parallel with nothing it depends on, and waits at
 * most PRESENTATION_BUDGET_MS. Anything other than a timely, well-formed 200
 * resolves to null and the buyer gets today's page untouched: a 404 (not built,
 * switched to the plain page, unknown token), a slow or failed request, or a
 * body this version does not understand.
 *
 * The request is a simple GET with no custom headers and no credentials, so the
 * browser sends no CORS preflight and the response stays cacheable.
 */

export const PRESENTATION_BUDGET_MS = 1200
export const PRESENTATION_VERSION = 1

const PUBLIC_PATH = '/api/v1/public/line-sheets/'
const TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const trimBase = base => String(base || '').replace(/\/+$/, '')

/**
 * The public route root for one sheet's token (`<base>/api/v1/public/line-sheets/<token>`),
 * or null when the token or base cannot be used. Shared with the feedback routes (KP-4572).
 */
export function publicSheetRoot(plgBaseUrl, token) {
  const base = trimBase(plgBaseUrl)
  if (!base || !TOKEN_PATTERN.test(String(token || ''))) return null
  return `${base}${PUBLIC_PATH}${token.toLowerCase()}`
}

/** The presentation URL, or null when the token or base cannot be used. */
export function presentationUrl(plgBaseUrl, token, trackedLinkId = '', version = '') {
  const root = publicSheetRoot(plgBaseUrl, token)
  if (!root) return null

  const params = []
  if (trackedLinkId) params.push(`tl=${encodeURIComponent(trackedLinkId)}`)
  if (version) params.push(`v=${encodeURIComponent(version)}`)
  const query = params.length ? `?${params.join('&')}` : ''

  return `${root}/presentation${query}`
}

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const hasSkin = data => isObject(data.skin) && isObject(data.skin.tokens)

const hasShape = data => hasSkin(data) && isObject(data.brand) && Array.isArray(data.sections)

/** True when the body is a presentation this page knows how to render. */
export function isUsablePresentation(data) {
  return isObject(data) && data.v === PRESENTATION_VERSION && hasShape(data)
}

/**
 * An https URL as is, a root-relative one on the PLG origin, anything else
 * "". plg-go-be sends root-relative asset URLs when it does not know its own
 * public origin.
 */
const isRootRelative = raw => raw.startsWith('/') && !raw.startsWith('//')

function httpsOnly(raw) {
  try {
    return new URL(raw).protocol === 'https:' ? raw : ''
  } catch (e) {
    return ''
  }
}

function resolveUrl(url, base) {
  const raw = String(url || '')
  if (!isRootRelative(raw)) return httpsOnly(raw)
  return base ? `${base}${raw}` : ''
}

const listOf = value => (Array.isArray(value) ? value : [])

/** Each entry with its url resolved; entries left with no usable url dropped. */
const withResolvedUrls = (entries, base) => listOf(entries)
  .filter(isObject)
  .map(entry => ({ ...entry, url: resolveUrl(entry.url, base) }))
  .filter(entry => entry.url)

function absolutizeFont(font, base) {
  return isObject(font) ? { ...font, src: withResolvedUrls(font.src, base) } : {}
}

function absolutizeLogo(logo, base) {
  if (!isObject(logo)) return null
  const url = resolveUrl(logo.url, base)
  return url ? { ...logo, url } : null
}

/** A new presentation whose asset URLs are absolute and safe to load. */
export function absolutizePresentation(data, plgBaseUrl) {
  const base = trimBase(plgBaseUrl)
  return {
    ...data,
    cover: withResolvedUrls(data.cover, base),
    skin: {
      ...data.skin,
      heading_font: absolutizeFont(data.skin.heading_font, base),
      body_font: absolutizeFont(data.skin.body_font, base),
      logo: absolutizeLogo(data.skin.logo, base),
    },
  }
}

/** The promise, or null once budgetMs passes (the request is then aborted). */
export function withBudget(promise, controller, budgetMs) {
  let timer
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => {
      if (controller) controller.abort()
      resolve(null)
    }, budgetMs)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

async function readPresentation(url, fetchImpl, signal) {
  const res = await fetchImpl(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    signal,
  })
  if (!res || !res.ok || res.status !== 200) return null
  return res.json()
}

/**
 * The token's presentation, or null. Never throws: the buyer's page must not
 * depend on this call in any way.
 */
export async function fetchLineSheetPresentation({
  plgBaseUrl,
  token,
  trackedLinkId = '',
  version = '',
  budgetMs = PRESENTATION_BUDGET_MS,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
}) {
  const url = presentationUrl(plgBaseUrl, token, trackedLinkId, version)
  if (!url || !fetchImpl) return null

  const controller = typeof AbortController === 'function' ? new AbortController() : null
  try {
    const data = await withBudget(
      readPresentation(url, fetchImpl, controller ? controller.signal : undefined),
      controller,
      budgetMs,
    )
    return isUsablePresentation(data) ? absolutizePresentation(data, plgBaseUrl) : null
  } catch (e) {
    return null
  }
}

/**
 * The fetch arguments a public line sheet route carries in its query: the
 * tracked link id (`ks`, KP-3741) and the studio's cache-busting version
 * (`lsv`, sent by the PLG line sheet studio's preview).
 */
export function presentationQuery(query = {}) {
  const pick = key => (typeof query[key] === 'string' ? query[key] : '')
  return { trackedLinkId: pick('ks'), version: pick('lsv') }
}
